/**
 * The one place that answers "where does she live?" (Issue #533).
 *
 * Before this, her location lived in five places and no two agreed:
 *
 *   1. `user_preferences.zip_code`               ← Settings + Pro Setup wrote it, NOTHING read it
 *   2. `briefing_prefs.weather.zip`              ← Morning Notes weather only
 *   3. `scheduled_tasks.config.location`         ← copied into each card at creation
 *   4. localStorage["kin_kroger_zip"]            ← Kroger only
 *   5. component state                           ← Summer/Garden planners, thrown away
 *
 * So she typed her zip during Pro setup into a column with no readers, then got
 * asked for City and State from scratch by Event Finder. This module makes (1)
 * canonical and everything else read from it.
 *
 * ⚠️ Two rules, both load-bearing:
 *
 * - **Canonical WRITE target is `user_preferences.zip_code` only.** The weather
 *   zip is a READ fallback and is never written here — `dispatch-notifications`
 *   caches geocoded lat/lon back into that object and clearing them out from
 *   under it would make weather report the wrong city forever
 *   (see BriefingBuilder.tsx's handleZipChange for why).
 *
 * - **Nothing snapshots this.** Cards live-read at run time; `buildCardLocation`
 *   returns null when what she typed still equals her saved default, so the card
 *   row stores no location at all and follows her profile. Only a genuine
 *   per-card override persists. This is the #530 lesson (a stored copy of
 *   user-editable data is a bug with a delay on it) applied up front.
 */

export interface CityState {
  city: string;
  stateCode: string;
}

const ZIP_RE = /^\d{5}$/;

/** localStorage cache is keyed BY THE ZIP, so changing her zip invalidates it for free. */
const CACHE_PREFIX = "kin_zip_place:";

/** Give up on the geocode rather than leave a sheet waiting on it. */
const LOOKUP_TIMEOUT_MS = 6000;

/** Returns the 5-digit zip, or null for anything else (partial, empty, non-US). */
export function normalizeZip(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return ZIP_RE.test(trimmed) ? trimmed : null;
}

export interface LocationPrefsShape {
  zip_code?: string | null;
  briefing_prefs?: unknown;
}

/**
 * Canonical READ: her saved zip, or null if she's never given us one.
 *
 * Falls back to the Morning Notes weather zip because a mom who only ever
 * switched on Daily weather has given us her location — she just gave it to a
 * different form. Not falling back would mean asking her a third time, which is
 * the exact complaint this issue is about.
 */
export function resolveSavedZip(prefs: LocationPrefsShape | null | undefined): string | null {
  if (!prefs) return null;
  const primary = normalizeZip(prefs.zip_code);
  if (primary) return primary;
  const briefing = prefs.briefing_prefs as { weather?: { zip?: unknown } } | null | undefined;
  return normalizeZip(briefing?.weather?.zip);
}

/**
 * Pulls city + 2-letter state out of a zippopotam.us response.
 *
 * Same service `dispatch-notifications` already geocodes weather with
 * (index.ts, `https://api.zippopotam.us/us/{zip}`) — free, no key, CORS-open,
 * and already a trusted dependency here. A 404 body has no `places`, which
 * lands on null rather than throwing.
 */
export function parseZippopotam(payload: unknown): CityState | null {
  const place = (payload as { places?: Array<Record<string, unknown>> } | null)?.places?.[0];
  if (!place) return null;
  const city = String(place["place name"] ?? "").trim();
  const stateCode = String(place["state abbreviation"] ?? "").trim().toUpperCase();
  if (!city || stateCode.length !== 2) return null;
  return { city, stateCode };
}

/** Case- and whitespace-insensitive: "lexington "/"ky" is not an override of "Lexington"/"KY". */
export function isSameCityState(a: CityState | null | undefined, b: CityState | null | undefined): boolean {
  if (!a || !b) return false;
  return (
    a.city.trim().toLowerCase() === b.city.trim().toLowerCase() &&
    a.stateCode.trim().toUpperCase() === b.stateCode.trim().toUpperCase()
  );
}

/**
 * What a card should store in `config.location`.
 *
 * null means "follow her saved location" — the card row holds nothing and
 * `run-scheduled-tasks` resolves it fresh on every run, so changing her zip in
 * Settings moves every non-overridden card with it. A non-null return is a
 * deliberate per-card override and stays put no matter what she changes later.
 *
 * ⚠️ CITY DECIDES. An override needs a non-blank city, because that is the rule
 * the runner enforces — `readConfigLocation` in
 * `supabase/functions/run-scheduled-tasks/taskLocation.ts` ignores any stored
 * location whose city is blank. Storing `{ city: "", stateCode: "KY" }` would
 * make the detail sheet show a blank city next to "KY" while the run searched
 * her profile city instead: a card displaying one thing and doing another.
 * These two functions have to agree on what an override IS.
 */
export function buildCardLocation(
  entered: CityState,
  saved: CityState | null | undefined
): CityState | null {
  const city = entered.city.trim();
  if (!city) return null;
  const stateCode = entered.stateCode.trim().toUpperCase();
  const normalized = { city, stateCode };
  return isSameCityState(normalized, saved) ? null : normalized;
}

/**
 * What a card's location field should DISPLAY: its own override if it has one,
 * otherwise her saved default. Keeps a live-read card from rendering two blank
 * boxes when the value it actually uses is sitting right there in her profile.
 *
 * Same CITY DECIDES rule as `buildCardLocation`, for the same reason: this must
 * show what the run will actually use. A legacy row of `{ city: "" }` — which
 * the runner ignores — displays her saved location, not a blank box.
 */
export function displayCardLocation(
  stored: Partial<CityState> | null | undefined,
  saved: CityState | null | undefined
): CityState {
  const city = (stored?.city ?? "").trim();
  const stateCode = (stored?.stateCode ?? "").trim();
  if (city) return { city, stateCode: stateCode.toUpperCase() };
  return saved ? { ...saved } : { city: "", stateCode: "" };
}

/* ------------------------------------------------------------------ cache -- */

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    // Safari private mode throws on access, not just on write.
    return null;
  }
}

export function readCachedZipLocation(zip: string): CityState | null {
  const store = safeStorage();
  if (!store) return null;
  try {
    const raw = store.getItem(CACHE_PREFIX + zip);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.city !== "string" || typeof parsed?.stateCode !== "string") return null;
    if (!parsed.city || !parsed.stateCode) return null;
    return { city: parsed.city, stateCode: parsed.stateCode };
  } catch {
    return null;
  }
}

function writeCachedZipLocation(zip: string, place: CityState): void {
  const store = safeStorage();
  if (!store) return;
  try {
    store.setItem(CACHE_PREFIX + zip, JSON.stringify(place));
  } catch {
    /* quota / private mode — the lookup just isn't cached, which is survivable */
  }
}

/**
 * zip → { city, stateCode }, cached. Resolves null on anything unexpected;
 * every caller treats null as "leave the field empty and ask her", per the
 * issue's "prefill only, never invent".
 */
export async function lookupZipLocation(
  zip: string,
  fetchImpl: typeof fetch = fetch
): Promise<CityState | null> {
  const normalized = normalizeZip(zip);
  if (!normalized) return null;

  const cached = readCachedZipLocation(normalized);
  if (cached) return cached;

  // Bounded on purpose. This runs on a phone on cellular, and useSavedLocation
  // reports `loading` until it settles — an unbounded request that never
  // resolves would leave a sheet waiting on a prefill forever rather than
  // falling back to asking her. Timing out lands on null, which every caller
  // already treats as "leave the field empty and ask".
  const controller = typeof AbortController === "undefined" ? null : new AbortController();
  const timer = controller ? setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS) : null;
  try {
    const resp = await fetchImpl(`https://api.zippopotam.us/us/${encodeURIComponent(normalized)}`, {
      signal: controller?.signal,
    });
    if (!resp.ok) return null;
    const place = parseZippopotam(await resp.json());
    if (place) writeCachedZipLocation(normalized, place);
    return place;
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
